import SwiperCarousel from '../utils/swiper-carousel';

export default (element, data) => {
  // ELEMENTS:
  const body = document.querySelector('body');
  const promoBar = document.querySelector('.promo-bar');
  const submenus = element.querySelectorAll('[data-submenu]');
  const header = element.parentElement;
  const headerPadding = 24;

  const mobileMenuTrigger = element.querySelector('[data-nav-trigger]');
  const mobileMenuInner = element.querySelector('[data-mobile-menu-inner]');
  const expandableMenus = element.querySelectorAll('[data-expandable-nav]');
  const closeMenuTrigger = element.querySelector('[data-close-menu]');
  const desktopNavItems = element.querySelectorAll('[data-nav-link]');
  const desktopParentNavItems = element.querySelectorAll('[data-nav-item]');
  const mainContentOverlay = element.querySelector('[data-main-content-overlay]');

  // SEARCH RELATED ELEMENTS
  const searchTrigger = element.querySelectorAll('[data-search-trigger]');
  const searchDrawer = element.querySelector('[data-search-drawer]');
  const searchDrawerClose = element.querySelector('[data-search-drawer-close]');
  const searchDrawerSlider = element.querySelector('[data-search-drawer-slider]');
  const searchDrawerScrollbal = element.querySelector('[data-search-drawer-scrollbar]');
  const searchDrawerSliderPrev = element.querySelector('[data-search-drawer-slider-prev]');
  const searchDrawerSliderNext = element.querySelector('[data-search-drawer-slider-next]');

  // Near top with other element declarations
  const overlayMenu = element.classList.contains('overlay-menu') ? element : null;
  let headerStateTimeout;
  let rafId;

  // Throttle helper
  const throttle = (func, limit) => {
    let inThrottle;
    let lastFunc;
    let lastRan;

    return function throttled(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        lastRan = Date.now();
        inThrottle = true;
      } else {
        clearTimeout(lastFunc);
        lastFunc = setTimeout(() => {
          if (Date.now() - lastRan >= limit) {
            func.apply(this, args);
            lastRan = Date.now();
          }
        }, limit - (Date.now() - lastRan));
      }
    };
  };

  // Handle header transparency - only if overlay menu exists
  const handleHeaderScroll = throttle(() => {
    if (!overlayMenu) return;

    const scrollThreshold = 220;
    const isScrolled = window.scrollY > scrollThreshold;

    // Force a class check/update on each scroll
    if (isScrolled) {
      overlayMenu.classList.remove('bg-transparent');
      overlayMenu.classList.add('bg-white');
    } else {
      overlayMenu.classList.remove('bg-white');
      overlayMenu.classList.add('bg-transparent');
    }

    // Cancel any existing timeout/RAF
    if (headerStateTimeout) clearTimeout(headerStateTimeout);
    if (rafId) cancelAnimationFrame(rafId);

    // Start a brief monitoring period after scroll
    let checkCount = 0;
    const checkHeaderState = () => {
      checkCount += 1;
      if (checkCount > 10) return; // Stop after ~10 frames (~160ms)

      if (window.scrollY <= scrollThreshold && overlayMenu.classList.contains('bg-white')) {
        overlayMenu.classList.remove('bg-white');
        overlayMenu.classList.add('bg-transparent');
      }
      rafId = requestAnimationFrame(checkHeaderState);
    };

    headerStateTimeout = setTimeout(() => {
      rafId = requestAnimationFrame(checkHeaderState);
    }, 100); // Start monitoring 100ms after last scroll event
  }, 50);

  // EVENTS:
  const headerEventsHandler = () => {
    const closeMobileMenu = () => {
      // Close any expanded mobile navigation sections before closing the menu
      const expandedSections = element.querySelectorAll('[data-mobile-menu-link].expanded');
      expandedSections.forEach((section) => {
        const expandableButton = section.querySelector('[data-expandable-nav]');
        if (expandableButton) {
          expandableButton.click();
        }
      });
      
      body.classList.remove('open-menu');
      body.classList.remove('has-expanded-mobile-nav-section');
    };

    const toggleMobileMenu = () => {
      body.classList.toggle('open-menu');

      // this logic fixes a safari iOS bug - see https://meetdomaine.atlassian.net/browse/SAJ-1454
      setTimeout(() => {
        if (body.classList.contains('open-menu')) {
          body.querySelectorAll('[data-mobile-menu-link]')
            .forEach((link) => {
              link.classList.add('w-full');
            });
        } else {
          body.querySelectorAll('[data-mobile-menu-link]')
            .forEach((link) => {
              link.classList.remove('w-full');
            });
        }
      }, 75);

      updateHeaderPosition();
      updateClosingEvent();
    };

    const updateClosingEvent = () => {
      if (body.classList.contains('open-menu')) {
        document.addEventListener('keyup', (e) => {
          if (e.keyCode === 27) {
            closeMobileMenu();
          }
        });
      } else {
        document.removeEventListener('keyup', closeMobileMenu);
      }
    };

    const updateHeaderPosition = () => {
      const isDesktop = data.layout.bps.above.xl.matches;
      const promoBarHeight = promoBar ? promoBar.offsetHeight : 0;

      header.style.top = isDesktop ? `${promoBarHeight}px` : '0px';
      if (isDesktop) {
        submenus.forEach((menu) => {
          const menuStyle = menu.style;
          menuStyle.top = `${promoBarHeight + headerPadding}px`;
        });

        closeMobileMenu();
      }

      if (!isDesktop) {
        const mobilePromoBarHeight = window.scrollY <= 0 ? promoBarHeight : 0;
        const menuInnerHeight = window.innerHeight - mobilePromoBarHeight - header.offsetHeight;
        mobileMenuInner.style.height = `${menuInnerHeight}px`;
      }
    };

    expandableMenus.forEach((menu) => {
      menu.addEventListener('click', () => {
        expandableMenus.forEach((el) => {
          if (el === menu) {
            el.parentElement.classList.toggle('expanded');
          } else {
            el.parentElement.classList.remove('expanded');
          }
        });
        
        // Check if any menu is expanded and update body class accordingly
        const hasExpandedMenu = Array.from(expandableMenus).some(el => 
          el.parentElement.classList.contains('expanded')
        );
        
        if (hasExpandedMenu) {
          body.classList.add('has-expanded-mobile-nav-section');
          // it should scroll to the top of the expanded [data-mobile-menu-link] with the expanded class
          const expandedLink = document.querySelector('[data-mobile-menu-link].expanded');
          if (expandedLink) {
            expandedLink.scrollIntoView({ behavior: 'smooth' });
          }
        } else {
          body.classList.remove('has-expanded-mobile-nav-section');
        }
      });
    });

    desktopNavItems.forEach((item) => {
      item.addEventListener('focus', () => {
        desktopParentNavItems.forEach((el) => {
          el.classList.remove('expanded');
        });

        if (item.tagName === 'SPAN') {
          item.parentElement.classList.add('expanded');
          mainContentOverlay.classList.remove('hidden');
        } else mainContentOverlay.classList.add('hidden');

        document.addEventListener('keyup', (e) => {
          if (e.keyCode === 27) {
            item.parentElement.classList.remove('expanded');
          }
        });
      });
    });

    desktopParentNavItems.forEach((item) => {
      if (item.querySelector('[data-nav-link]').tagName !== 'SPAN') return;

      item.addEventListener('mouseover', () => {
        item.classList.add('expanded');
        mainContentOverlay.classList.remove('hidden');
      });

      item.addEventListener('mouseleave', () => {
        item.classList.remove('expanded');
        mainContentOverlay.classList.add('hidden');
      });
    });

    // Set initial state and add scroll listener only if overlay menu exists
    if (overlayMenu) {
      handleHeaderScroll();
      window.addEventListener('scroll', handleHeaderScroll);
    }

    updateHeaderPosition();
    closeMenuTrigger.addEventListener('click', closeMobileMenu);
    mobileMenuTrigger.addEventListener('click', toggleMobileMenu);
    window.addEventListener('resize', updateHeaderPosition);

    // Update cleanup to only remove scroll listener if it was added
    return () => {
      if (overlayMenu) {
        window.removeEventListener('scroll', handleHeaderScroll);
        if (headerStateTimeout) clearTimeout(headerStateTimeout);
        if (rafId) cancelAnimationFrame(rafId);
      }
      window.removeEventListener('resize', updateHeaderPosition);
    };
  };

  // SEARCH EVENTS:
  const searchHandler = () => {
    if (!searchTrigger || !searchDrawer) return;

    const openSearch = () => {
      document.body.classList.add('no-scroll');
      searchDrawer.classList.toggle('invisible');
      searchDrawer.classList.toggle('pointer-events-none');
    };

    const closeSearch = () => {
      document.body.classList.remove('no-scroll');
      searchDrawer.classList.toggle('invisible');
      searchDrawer.classList.toggle('pointer-events-none');
    };

    searchTrigger.forEach((searchEl) => {
      searchEl.addEventListener('click', openSearch);

      // if search trigger has .custom-search-drawer__input class then it should openSearch on focus
      if (searchEl.classList.contains('custom-search-drawer__input')) {
        searchEl.addEventListener('focus', openSearch);
      }
    });

    searchDrawerClose.addEventListener('click', closeSearch);

    searchDrawer.addEventListener('click', (e) => {
      if (e.target.dataset.searchDrawer) closeSearch();
    });
  };

  const initSearchResultSlider = () =>
    !!searchDrawerSlider &&
    new SwiperCarousel(searchDrawerSlider, {
      slidesPerView: 2.1,
      spaceBetween: 10.5,
      allowTouchMove: true,
      breakpoints: {
        768: {
          slidesPerView: 3.2,
        },
        1024: {
          spaceBetween: 15,
          slidesPerView: 3.8,
        },
        1440: {
          spaceBetween: 15,
          slidesPerView: 4.8,
        },
      },
      navigation: {
        nextEl: searchDrawerSliderNext,
        prevEl: searchDrawerSliderPrev,
      },
      scrollbar: {
        el: searchDrawerScrollbal,
        draggable: true,
      },
      mousewheel: {
        forceToAxis: true,
      },
    });

  const init = () => {
    headerEventsHandler();
    searchHandler();
    initSearchResultSlider();
  };

  init();
};
